import { dashCase } from "./string-utils";

// Determines whether an element has a slot. If name is specified, the function will look for a corresponding named
// slot, otherwise it will look for a "default" slot (e.g. a non-empty text node or an element with no slot attribute).
export function hasSlot(el: HTMLElement, name?: string) {
  // Look for a named slot
  if (name) {
    return el.querySelector(`[slot="${name}"]`) !== null;
  }

  // Look for a default slot
  return Array.from(childNodes(el)).some((node) => {
    if (node.nodeType === node.TEXT_NODE && node.textContent.trim() !== "") {
      return true;
    }

    if (node.nodeType === node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (!el.hasAttribute("slot")) {
        return true;
      }
    }

    return false;
  });
}

export function setPropsIfNull(element: Element, attributes: any) {
  Object.keys(attributes).forEach((attribute) => {
    if (element.getAttribute(dashCase(attribute)) === null) {
      element[attribute] = attributes[attribute];
    }
  });
}

export function setProps(element: Element, attributes: any) {
  Object.keys(attributes).forEach((attribute) => {
    element[attribute] = attributes[attribute];
  });
}

export function isStzhElement(element: Element) {
  if (!element || element.nodeType !== 1) {
    return false;
  }

  return element.tagName.toLowerCase().indexOf("stzh-") === 0;
}

// returns content of <meta name="" content=""> tags or '' if empty/non existant
export function getMeta(name) {
  const meta = document.querySelector(
    `meta[name="${name}"],[property="${name}"]`,
  );
  return meta ? meta.getAttribute("content") : "";
}

export function parents(node: Element | Node) {
  const parents = [];

  while (node) {
    parents.unshift(node);
    node = node.parentNode;
  }

  return parents;
}

export function childNodes(node: Element) {
  const childNodes = node.childNodes;

  // check if element is stencil element without shadow dom
  // and then detect elements that were slotted into the element
  if (node["s-sc"]) {
    const result = [];

    for (let i = 0; i < childNodes.length; i++) {
      const slot = childNodes[i]["s-nr"];

      if (slot) {
        result.push(slot);
      }
    }

    return result;
  }

  return Array.from(childNodes);
}

export function isVisible(element: HTMLElement): boolean {
  // @ts-ignore
  if (typeof element.checkVisibility !== "undefined") {
    // @ts-ignore
    return element.checkVisibility({
      checkOpacity: false,
      checkVisibilityCSS: false,
    });
  } else {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  }
}

export function offset(element): { top: number; left: number } {
  if (!element.getClientRects().length) {
    return { top: 0, left: 0 };
  }

  let rect = element.getBoundingClientRect();
  let win = element.ownerDocument.defaultView;

  return {
    top: rect.top + win.pageYOffset,
    left: rect.left + win.pageXOffset,
  };
}

export async function raf() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

export async function setTimeout(time: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(() => {
      resolve();
    }, time);
  });
}

export enum StzhEnvironmentEnum {
  UNKNOWN = "UNKNOWN",
  DEV = "DEV",
  UAT = "UAT",
  PROD = "PROD",
}

export function getStzhEnvironment(): StzhEnvironmentEnum {
  const url = window.location.href;

  switch (true) {
    // DEV
    case url.includes("localhost"):
    case url.includes("test.stadt-zuerich.loc"):
    case /\.dev\.cmp\.szh\.loc/.test(url):
      return StzhEnvironmentEnum.DEV;

    // UAT
    case url.includes("integ.stadt-zuerich.ch"):
    case /\.uat\.cmp\.szh\.loc/.test(url):
      return StzhEnvironmentEnum.UAT;

    // PROD
    case url.includes("https://www.stadt-zuerich.ch"):
    case url.includes("designsystem.stadt-zuerich.ch"):
    case /\.cmp\.szh\.loc/.test(url):
      return StzhEnvironmentEnum.PROD;

    // Fallback
    default:
      return StzhEnvironmentEnum.UNKNOWN;
  }
}

export function addWordBreakToLinks(links: HTMLElement[]) {
  links.forEach((link) => {
    if (link.textContent?.includes("_")) {
      link.innerHTML = link.textContent.replace(/_/g, "_<wbr>");
    }
  });
}

export function getTheme() {
  const htmlElement = document.documentElement;
  return Array.from(htmlElement.classList).find((className) =>
    className.startsWith("stzh-theme-"),
  );
}
